import type { OpportunitiesResponse, SingleOpportunity } from '@swf/generated/types/api/sports';

import { initialState } from '../../betSlip';
import type { BetSlipState } from '../../types/betSlip';
import { generateAcceptedOdds } from '../../utils/betSlip/generateAcceptedOdds';

const betSlipUpdated = (state: BetSlipState, payload: OpportunitiesResponse): BetSlipState => {
  let { selectedPromotionPicker } = state;
  const isSinglePromotionPicker = payload.opportunities.SINGLE.some(
    (singleOpportunity) => selectedPromotionPicker === singleOpportunity.id
  );
  const isMultiplePromotionPicker = payload.opportunities.MULTIPLE.some(
    (multipleOpportunity) => selectedPromotionPicker === multipleOpportunity.id
  );

  // Reset the promotion picker state if a multiple opportunity is no longer returned
  if (!isSinglePromotionPicker && !isMultiplePromotionPicker) {
    ({ selectedPromotionPicker } = initialState);
  }

  const updatedSingles = payload.opportunities.SINGLE.map((opportunity) => {
    // Patch suspended or unavailable opportunities with previous data
    if (!['UNAVAILABLE', 'SUSPENDED'].includes(opportunity.status)) {
      return opportunity;
    }

    // Reset the promotion picker state if that opportunity becomes UNAVAILABLE or SUSPENDED
    if (selectedPromotionPicker === opportunity.id) {
      ({ selectedPromotionPicker } = initialState);
    }

    // There is enough information to display an opportunity
    if (opportunity.name && opportunity.description) return opportunity;

    const matchedOpp = state.opportunities.SINGLE.find(
      (stateOpportunity) => stateOpportunity.id === opportunity.id
    );
    // If there is no previous opportunity in the state and not enough information to display an opportunity
    // This edge case happens when you select selection but the opportunity is closed
    if (!matchedOpp) return undefined;

    return {
      ...matchedOpp,
      status: opportunity.status,
      failureCode: 'failureCode' in opportunity ? opportunity.failureCode : undefined
    };
  }).filter((opp): opp is SingleOpportunity => !!opp);

  const allOpportunities = [...updatedSingles, ...payload.opportunities.MULTIPLE];
  const opportunitiesIds = allOpportunities.map(({ id }) => id);

  // Add new accepted odds
  const acceptedOdds = generateAcceptedOdds(state.acceptedOdds, allOpportunities);

  // Remove accepted odds if the opportunity is no longer in the betslip
  Object.keys(acceptedOdds).forEach((id) => {
    if (!opportunitiesIds.includes(id)) delete acceptedOdds[id];
  });

  return {
    ...state,
    acceptedOdds,
    opportunities: {
      SINGLE: updatedSingles,
      MULTIPLE: payload.opportunities.MULTIPLE
    },
    wallets: payload.wallets,
    selectedPromotionPicker,
    hasInvalidCombination: payload.hasInvalidCombination,
    requiredPlacementCurrency: payload.requiredPlacementCurrency,
    dailyMaxPayoutLimit: payload.dailyMaxPayoutLimit,
    fetchStatus: 'READY'
  };
};

export default betSlipUpdated;
